import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
} from "react";
import { Command as CommandPrimitive } from "cmdk";

import { type DialogProps } from "@radix-ui/react-dialog";
import { Dialog, DialogContent } from "@vega-core/components/shadcn/dialog";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const Command = forwardRef<
  ElementRef<typeof CommandPrimitive>,
  ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    className={cnTwMerge(
      "flex h-full w-full flex-col overflow-hidden rounded-companyBorderRadius text-text/primary bg-background/primary",
      className,
    )}
    ref={ref}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

type CommandDialogProps = DialogProps & {};

const CommandDialog = ({ children, ...props }: CommandDialogProps) => (
  <Dialog data-testid="common-dialog" {...props}>
    <DialogContent
      className="overflow-hidden p-0 shadow-lg z-palette"
      hideCloseButton
      overlayClassName="z-palette"
    >
      <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-neutral-500 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5 dark:[&_[cmdk-group-heading]]:text-neutral-400">
        {children}
      </Command>
    </DialogContent>
  </Dialog>
);

const CommandInput = forwardRef<
  ElementRef<typeof CommandPrimitive.Input>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    compact?: boolean;
  }
>(({ className, compact, ...props }, ref) => (
  <CommandPrimitive.Input
    className={cnTwMerge(
      "flex h-11 w-full gap-2 items-center break-all justify-between outline:none !text-[14px] body-base-medium rounded-companyBorderRadius border border-border/primary pl-5 pr-4 py-2.5 body-base-regular text-text/primary placeholder:text-text/secondary disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 focus:border-brand-default outline-none focus:outline-none focus:ring-0 focus:ring-brand-default focus:ring-offset-0 body-base-medium ",
      className,
      { "h-8": compact },
    )}
    ref={ref}
    {...props}
  />
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = forwardRef<
  ElementRef<typeof CommandPrimitive.List>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    className={cnTwMerge(
      "max-h-[300px] overflow-y-auto overflow-x-hidden",
      className,
    )}
    ref={ref}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = forwardRef<
  ElementRef<typeof CommandPrimitive.Empty>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    className="py-6 text-center text-sm"
    ref={ref}
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = forwardRef<
  ElementRef<typeof CommandPrimitive.Group>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    className={cnTwMerge("overflow-hidden p-1 text-text/primary", className)}
    ref={ref}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = forwardRef<
  ElementRef<typeof CommandPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    className={cnTwMerge(
      "-mx-1 h-px bg-neutral-200 dark:bg-neutral-800",
      className,
    )}
    ref={ref}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = forwardRef<
  ElementRef<typeof CommandPrimitive.Item>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    className={cnTwMerge(
      "relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 body-base-medium outline-none aria-selected:bg-background/secondary aria-selected:text-text/primary data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className,
    )}
    ref={ref}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cnTwMerge(
      "ml-auto tracking-widest body-base-semibold text-text/secondary",
      className,
    )}
    {...props}
  />
);
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
